import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAlertsHistory, markAlertsRead, resolveAlerts } from '../controllers/alertController';
import FieldHelp from '../help/FieldHelp';

// Hover help for each alert type (text in src/help/notifications.js).
const ALERT_TYPE_HELP = {
    power_exceeded: 'notifications.powerExceeded',
    outside_hours: 'notifications.outsideHours',
    inactive_during_hours: 'notifications.inactiveDuringHours',
};
const alertTypeHelpId = (alertType) => ALERT_TYPE_HELP[alertType] || 'notifications.otherAlert';

const AlertBox = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [resolvingIds, setResolvingIds] = useState({});

    const getUsername = () => {
        try {
            const stored = localStorage.getItem("user");
            if (stored) {
                const user = JSON.parse(stored);
                return localStorage.getItem("username") || user?.username || user?.userName;
            }
        } catch (e) {}
        return localStorage.getItem("username");
    };

    const fetchAlerts = async () => {
        const username = getUsername();
        if (!username) return;
        setLoading(true);
        try {
            const data = await getAlertsHistory(username);
            setAlerts(data.alerts || []);
        } catch (e) {
            console.error("Failed to fetch alerts:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) fetchAlerts();
    }, [isOpen]);

    const activeAlerts = alerts.filter(a => !a.is_resolved);
    const resolvedAlerts = alerts.filter(a => a.is_resolved);
    const unreadAlerts = activeAlerts.filter(a => !a.is_read);

    const handleMarkAllRead = async () => {
        const unreadIds = unreadAlerts.map(a => a.id);
        if (unreadIds.length === 0) return;
        try {
            await markAlertsRead(unreadIds);
            setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
        } catch (e) {
            console.error("Failed to mark read:", e);
        }
    };

    const handleResolve = async (e, alert) => {
        e.stopPropagation();
        if (!alert?.id || !alert?.meter_id) return;
        const username = getUsername();
        if (!username) return;
        setResolvingIds(prev => ({ ...prev, [alert.id]: true }));
        try {
            await resolveAlerts(username, alert.meter_id, [alert.id]);
            setAlerts(prev =>
                prev.map(a =>
                    a.id === alert.id
                        ? { ...a, is_resolved: true, resolved_reason: 'manual', resolved_at: new Date().toISOString() }
                        : a,
                ),
            );
        } catch (err) {
            console.error("Failed to resolve alert:", err);
        } finally {
            setResolvingIds(prev => {
                const next = { ...prev };
                delete next[alert.id];
                return next;
            });
        }
    };

    const handleClickAlert = async (id) => {
        const alert = alerts.find(a => a.id === id);
        if (alert && !alert.is_read) {
            try {
                await markAlertsRead([id]);
            } catch (e) {
                console.error("Failed to mark read:", e);
            }
        }
        navigate('/notifications');
        onClose();
    };

    const formatTime = (timestamp) => {
        const now = new Date();
        const alertTime = new Date(timestamp);
        const diffMs = now - alertTime;
        const diffMin = Math.floor(diffMs / 60000);
        const diffHr = Math.floor(diffMs / 3600000);
        const diffDay = Math.floor(diffMs / 86400000);

        if (diffMin < 1) return 'Just now';
        if (diffMin < 60) return `${diffMin} min ago`;
        if (diffHr < 24) return `${diffHr} hr ago`;
        if (diffDay < 7) return `${diffDay} day ago`;
        return alertTime.toLocaleDateString();
    };

    const getResolvedLabel = (reason) => {
        const labels = {
            machine_off: 'Machine turned off',
            machine_on: 'Machine turned on',
            hours_started: 'Back in working hours',
            hours_ended: 'Working hours ended',
            power_normal: 'Power back to normal',
            manual: 'Resolved by user',
        };
        return labels[reason] || reason;
    };

    const getSeverityColor = (alertType) => {
        if (alertType === 'power_exceeded') return '#ff4d4f';
        if (alertType === 'outside_hours') return '#faad14';
        if (alertType === 'inactive_during_hours') return '#fa8c16';
        return '#1890ff';
    };

    const getSeverityLabel = (alertType) => {
        if (alertType === 'power_exceeded') return 'High';
        if (alertType === 'outside_hours') return 'Warning';
        if (alertType === 'inactive_during_hours') return 'Inactive';
        return 'Info';
    };

    if (!isOpen) return null;

    return (
        <>
            <div style={styles.overlay} onClick={onClose} />
            <div style={styles.container}>
                <div style={styles.header}>
                    <span style={styles.headerTitle}><FieldHelp id="notifications.alertsPanel">Alerts</FieldHelp></span>
                    {unreadAlerts.length > 0 && (
                        <button style={styles.markAllBtn} onClick={handleMarkAllRead}>
                            Mark all read
                        </button>
                    )}
                </div>

                <div style={styles.content}>
                    {loading ? (
                        <div style={styles.empty}>Loading...</div>
                    ) : alerts.length === 0 ? (
                        <div style={styles.empty}>No alerts</div>
                    ) : (
                        <div style={styles.list}>
                            {activeAlerts.map((item) => (
                                <div key={item.id} style={{ ...styles.item, backgroundColor: item.is_read ? '#fff' : '#f6ffed' }} onClick={() => handleClickAlert(item.id)}>
                                    <div style={styles.itemHeader}>
                                        <span style={styles.itemTitle}><FieldHelp id={alertTypeHelpId(item.alert_type)}>{item.message}</FieldHelp></span>
                                        <span style={{
                                            ...styles.severityTag,
                                            backgroundColor: getSeverityColor(item.alert_type),
                                        }}>
                                            <FieldHelp id="notifications.severity">{getSeverityLabel(item.alert_type)}</FieldHelp>
                                        </span>
                                    </div>
                                    <div style={styles.itemFooter}>
                                        <div style={styles.itemTime}><FieldHelp id="notifications.alertTime">{formatTime(item.timestamp)}</FieldHelp></div>
                                        <button
                                            type="button"
                                            onClick={(e) => handleResolve(e, item)}
                                            disabled={!!resolvingIds[item.id]}
                                            style={{
                                                ...styles.resolveBtn,
                                                opacity: resolvingIds[item.id] ? 0.5 : 1,
                                                cursor: resolvingIds[item.id] ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            <FieldHelp id="notifications.resolveAction">{resolvingIds[item.id] ? 'Resolving...' : 'Resolve'}</FieldHelp>
                                        </button>
                                    </div>
                                    {!item.is_read && <div style={styles.dot} />}
                                </div>
                            ))}
                            {resolvedAlerts.slice(0, 5).map((item) => (
                                <div key={item.id} style={{ ...styles.item, opacity: 0.6, backgroundColor: '#fafafa' }} onClick={() => handleClickAlert(item.id)}>
                                    <div style={styles.itemHeader}>
                                        <span style={{ ...styles.itemTitle, color: '#8c8c8c' }}>{item.message}</span>
                                        <span style={styles.resolvedTag}><FieldHelp id="notifications.resolved">Resolved</FieldHelp></span>
                                    </div>
                                    {item.resolved_reason && (
                                        <div style={styles.resolvedReason}><FieldHelp id="notifications.resolvedReason">Reason</FieldHelp>: {getResolvedLabel(item.resolved_reason)}</div>
                                    )}
                                    <div style={styles.itemTime}><FieldHelp id="notifications.resolvedAt">Resolved {formatTime(item.resolved_at || item.timestamp)}</FieldHelp></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div style={styles.footer}>
                    <button
                        style={styles.viewAllButton}
                        onClick={() => {
                            navigate('/notifications');
                            onClose();
                        }}
                    >
                        View All
                    </button>
                </div>
            </div>
        </>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'transparent',
        zIndex: 998,
    },
    container: {
        position: 'absolute',
        top: '60px',
        right: '20px',
        width: '360px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 999,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '1px solid #f0f0f0',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: '#fafafa',
    },
    headerTitle: {
        fontWeight: 700,
        fontSize: '15px',
        color: '#262626',
    },
    markAllBtn: {
        border: 'none',
        backgroundColor: 'transparent',
        color: '#1BA08E',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '12px',
        outline: 'none',
    },
    content: {
        maxHeight: '350px',
        overflowY: 'auto',
        backgroundColor: '#fff',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
    },
    item: {
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer',
        position: 'relative',
    },
    itemHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '4px',
        gap: '8px',
    },
    itemTitle: {
        fontWeight: 500,
        fontSize: '13px',
        color: '#262626',
        flex: 1,
        lineHeight: '1.4',
    },
    itemTime: {
        fontSize: '11px',
        color: '#8c8c8c',
    },
    itemFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 4,
        gap: 8,
    },
    resolveBtn: {
        background: '#1BA08E',
        color: '#fff',
        border: 'none',
        borderRadius: 4,
        padding: '4px 10px',
        fontSize: 11,
        fontWeight: 600,
        flexShrink: 0,
    },
    dot: {
        position: 'absolute',
        top: '50%',
        right: '8px',
        transform: 'translateY(-50%)',
        width: '6px',
        height: '6px',
        backgroundColor: '#1BA08E',
        borderRadius: '50%',
    },
    severityTag: {
        fontSize: '10px',
        padding: '2px 6px',
        borderRadius: '4px',
        color: '#fff',
        textTransform: 'uppercase',
        fontWeight: 700,
        letterSpacing: '0.5px',
        flexShrink: 0,
    },
    resolvedTag: {
        fontSize: '10px',
        padding: '2px 6px',
        borderRadius: '4px',
        color: '#52c41a',
        backgroundColor: '#f6ffed',
        border: '1px solid #b7eb8f',
        textTransform: 'uppercase',
        fontWeight: 700,
        letterSpacing: '0.5px',
        flexShrink: 0,
    },
    resolvedReason: {
        fontSize: '11px',
        color: '#52c41a',
        fontWeight: 600,
        marginTop: '3px',
    },
    empty: {
        padding: '30px',
        textAlign: 'center',
        color: '#bfbfbf',
        fontSize: '14px',
    },
    footer: {
        padding: '10px',
        textAlign: 'center',
        borderTop: '1px solid #f0f0f0',
        backgroundColor: '#fafafa',
    },
    viewAllButton: {
        border: 'none',
        backgroundColor: 'transparent',
        color: '#1BA08E',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '13px',
        outline: 'none',
    },
};

export default AlertBox;
